/**
 * Login Page
 *
 * Email/password + Google OAuth. Apple-quiet aesthetic: single accent, no
 * gradients, finance-grade focus rings, accessible labels, keyboard-first.
 */

import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Loader2, LogIn } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/components/Toast';
import { AuthShell } from '@/components/AuthShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

function GoogleMark({ className }: { className?: string }) {
  // Inline Google "G" so we don't pull a brand SVG dependency.
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      className={className}
      fill="none"
    >
      <path
        d="M21.6 12.227c0-.704-.063-1.382-.181-2.034H12v3.85h5.382a4.6 4.6 0 0 1-1.995 3.018v2.51h3.232c1.891-1.742 2.981-4.305 2.981-7.344Z"
        fill="#4285F4"
      />
      <path
        d="M12 22c2.7 0 4.964-.895 6.619-2.43l-3.232-2.508c-.895.6-2.04.954-3.387.954-2.604 0-4.81-1.76-5.596-4.122H3.064v2.59A9.997 9.997 0 0 0 12 22Z"
        fill="#34A853"
      />
      <path
        d="M6.404 13.894A6.003 6.003 0 0 1 6.09 12c0-.659.114-1.298.314-1.894V7.516H3.064A9.997 9.997 0 0 0 2 12c0 1.614.385 3.14 1.064 4.484l3.34-2.59Z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.984c1.471 0 2.79.506 3.829 1.499l2.872-2.872C16.96 2.99 14.696 2 12 2A9.997 9.997 0 0 0 3.064 7.516l3.34 2.59C7.19 7.745 9.396 5.984 12 5.984Z"
        fill="#EA4335"
      />
    </svg>
  );
}

export default function Login() {
  const navigate = useNavigate();
  const { login, loginWithGoogle } = useAuth();
  const { error: showError, success: showSuccess } = useToast();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState<'email' | 'google' | null>(null);

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      showError('Missing fields', 'Please enter both email and password.');
      return;
    }

    setLoading('email');
    try {
      await login(email, password);
      showSuccess('Welcome back', 'Successfully signed in.');
      navigate('/dashboard');
    } catch (err: unknown) {
      const code = (err as { code?: string })?.code;
      let message = 'Please check your credentials and try again.';
      if (code === 'auth/user-not-found') message = 'No account found with this email.';
      else if (code === 'auth/wrong-password') message = 'Incorrect password.';
      else if (code === 'auth/invalid-email') message = 'Invalid email format.';
      else if (code === 'auth/too-many-requests')
        message = 'Too many failed attempts. Please try again later.';
      showError('Sign in failed', message);
    } finally {
      setLoading(null);
    }
  };

  const handleGoogleLogin = async () => {
    setLoading('google');
    try {
      await loginWithGoogle();
      showSuccess('Welcome back', 'Successfully signed in with Google.');
      navigate('/dashboard');
    } catch (err: unknown) {
      const message =
        (err as Error)?.message || 'Could not sign in with Google.';
      showError('Sign in failed', message);
    } finally {
      setLoading(null);
    }
  };

  const busy = loading !== null;

  return (
    <AuthShell
      title="Sign in to Aicostr"
      subtitle="Track every dollar your AI stack spends."
      footer={
        <>
          New here?{' '}
          <Link
            to="/signup"
            className="font-medium text-foreground underline-offset-4 hover:underline"
          >
            Create an account
          </Link>
        </>
      }
    >
      <div className="space-y-4">
        <Button
          type="button"
          variant="outline"
          size="lg"
          className="w-full"
          onClick={handleGoogleLogin}
          disabled={busy}
        >
          {loading === 'google' ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <GoogleMark className="size-4" />
          )}
          Continue with Google
        </Button>

        <div className="relative my-2 flex items-center">
          <div className="grow border-t border-hairline" />
          <span className="px-3 text-[11px] uppercase tracking-wider text-muted-foreground">
            or
          </span>
          <div className="grow border-t border-hairline" />
        </div>

        <form onSubmit={handleEmailLogin} className="space-y-4" noValidate>
          <div className="space-y-1.5">
            <Label htmlFor="login-email">Email</Label>
            <Input
              id="login-email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              disabled={busy}
            />
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label htmlFor="login-password">Password</Label>
              <Link
                to="/forgot-password"
                className="text-xs text-muted-foreground transition-colors hover:text-foreground"
                tabIndex={busy ? -1 : 0}
              >
                Forgot password?
              </Link>
            </div>
            <Input
              id="login-password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              disabled={busy}
            />
          </div>

          <Button type="submit" size="lg" className="w-full" disabled={busy}>
            {loading === 'email' ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <LogIn className="size-4" />
            )}
            {loading === 'email' ? 'Signing in…' : 'Sign in'}
          </Button>
        </form>
      </div>
    </AuthShell>
  );
}
